/**
 * Status line of one row in the sidebar automation overview:
 * status dot + label and the relative time of the last run.
 */
import { i18nTime, useI18n } from '@affine/i18n';
import clsx from 'clsx';
import type { FC } from 'react';

import type { AutomationDocSummary } from '../services/automation-config-api';
import * as styles from './workspace-automations-panel.css';
import * as overviewStyles from './workspace-overview.css';

export type AutomationOverviewStatus = Exclude<
  styles.AutomationOverviewTone,
  'failed'
>;

const STATUS_LABEL_KEYS = {
  active: 'com.streamlain.streamlain.workspace.automation-status.active',
  paused: 'com.streamlain.streamlain.workspace.automation-status.paused',
  manual: 'com.streamlain.streamlain.workspace.automation-status.manual',
  unset: 'com.streamlain.streamlain.workspace.automation-status.not-set-up',
} as const satisfies Record<AutomationOverviewStatus, string>;

/** No summary means the block has no saved backend config yet. */
export function resolveAutomationOverviewStatus(
  summary: AutomationDocSummary | undefined
): AutomationOverviewStatus {
  if (!summary) return 'unset';
  if (summary.triggerType === 'manual') return 'manual';
  return summary.enabled ? 'active' : 'paused';
}

export const AutomationOverviewStatusLine: FC<{
  summary: AutomationDocSummary | undefined;
}> = ({ summary }) => {
  const t = useI18n();
  const status = resolveAutomationOverviewStatus(summary);
  const lastRun = summary?.lastRun ?? null;
  const lastRunTime = lastRun
    ? i18nTime(lastRun.startedAt, {
        relative: { max: [1, 'day'], accuracy: 'minute' },
        absolute: { accuracy: 'day' },
      })
    : null;

  return (
    <div className={overviewStyles.itemMeta}>
      <span className={styles.status}>
        <span
          className={clsx(styles.statusDot, styles.statusDotTone[status])}
          aria-hidden="true"
        />
        {t[STATUS_LABEL_KEYS[status]]()}
      </span>
      {summary && (
        <>
          <span className={overviewStyles.metaSeparator} aria-hidden="true">
            ·
          </span>
          {lastRun && lastRunTime ? (
            lastRun.status === 'failed' ? (
              <span className={styles.lastRunFailed}>
                {t[
                  'com.streamlain.streamlain.workspace.automation-last-run-failed'
                ]({ time: lastRunTime })}
              </span>
            ) : (
              <span className={overviewStyles.metaSegment}>
                {t['com.streamlain.streamlain.workspace.automation-last-run']({
                  time: lastRunTime,
                })}
              </span>
            )
          ) : (
            <span className={overviewStyles.metaSegment}>
              {t['com.streamlain.streamlain.workspace.automation-never-run']()}
            </span>
          )}
        </>
      )}
    </div>
  );
};
